import axios, { AxiosResponse } from 'axios';
import {
  FieldValues,
  UseFormSetError,
  SubmitErrorHandler,
  FieldErrors,
} from 'react-hook-form';
import { AppThunk } from '../../app/store';
import {
  setError,
  setLiveListStatus,
  setLiveRegisterStatus,
  setLiveSaveButtonStatus,
} from '../../features/uiSlice';
import { InputData } from '../../props/LiveRegister';
import { SetLiveProfile, GetContract } from '../../types/API';
import { ToSetProfileStatusJap } from '../../utils/ChangeStatus';
import { setContract } from '../../features/userSlice';
import { EDITIONS, UI_STATUS } from '../../utils/Const';
import {
  LIVE_ADD_SAVE_FLOW_ERR_MSG,
  LIVE_SAVE_STANDBY_TYPE_ERR_MSG,
  LIVE_SAVE_MAX_CLIENT_ERR_MSG,
} from '../../utils/Message';
import setInputParams from '../../utils/LiveSaveUtil';
// import { smoothScrollToId } from '../../utils/SmoothScroll';

/** ライブプロファイル新規登録処理フロー */
const LiveSave =
  (
    data: InputData,
    edition: EDITIONS,
    encryptEdition: boolean,
    setSaveError: UseFormSetError<FieldValues>,
    onError: SubmitErrorHandler<InputData>,
    errors: FieldErrors<InputData>
  ): AppThunk =>
  async (dispatch, getState) => {
    const { user } = getState();
    // 動作モード設定 manualでない場合、契約会社情報取得API実行
    if (data.BaseSettingStandbyType !== 'manual') {
      dispatch(setLiveSaveButtonStatus(UI_STATUS.SAVING));
      // 契約会社情報取得API実行
      const responseGetContract: AxiosResponse<GetContract> = await axios.post(
        `${String(process.env.REACT_APP_BACKEND_API_ENDPOINT)}getContract`,
        {
          cid: user.contract.cid,
          userApiParams: user.user,
        },
        {
          headers: {
            accept: 'application/json',
            'Content-type': 'application/json',
          },
        }
      );
      dispatch(setContract({ ...responseGetContract.data }));
      let errorText = '';
      // 同時接続数チェック
      const maxClient =
        edition === EDITIONS.U_LIVE ? '0' : String(data.BaseSettingMaxClient);
      if (
        responseGetContract.data.liveInfo.nowClientCnt + Number(maxClient) >
        responseGetContract.data.liveInfo.maxClient
      ) {
        errorText = LIVE_SAVE_MAX_CLIENT_ERR_MSG;
        errors.BaseSettingMaxClient = {
          type: 'required',
          message: errorText,
        };
        setSaveError('BaseSettingMaxClient', errors.BaseSettingMaxClient);
      }
      // 配信開催数チェック
      if (
        responseGetContract.data.liveInfo.nowCnt + 1 >
        responseGetContract.data.liveInfo.cntLive
      ) {
        errorText = LIVE_SAVE_STANDBY_TYPE_ERR_MSG;
        errors.BaseSettingStandbyType = {
          type: 'required',
          message: errorText,
        };
        setSaveError('BaseSettingStandbyType', errors.BaseSettingStandbyType);
      }
      if (errorText !== '') {
        dispatch(setLiveSaveButtonStatus(UI_STATUS.PROCESSING));
        onError(errors);
        return false;
      }
    }
    // 処理中をセット
    dispatch(setLiveRegisterStatus(UI_STATUS.SAVING));
    try {
      // ライブプロファイル新規登録
      const formData = setInputParams(data, edition, encryptEdition);
      // 契約会社ID
      formData.append('cid', String(user.contract.cid));
      // userApiParams
      formData.append('userApiParams', user.user);
      const responseSetLiveProfileTmp: AxiosResponse<SetLiveProfile> =
        await axios.post(
          `${String(
            process.env.REACT_APP_BACKEND_API_ENDPOINT
          )}setProfileProcess`,
          formData,
          {
            headers: {
              accept: 'application/json',
              'Content-type': 'multipart/form-data',
            },
          }
        );
      if (responseSetLiveProfileTmp.data.errorCode !== 0) {
        // 異常終了
        // NOTE: 400系のクライアントサイドのエラーだが、エラー画面に遷移させないとチェックボックスの不具合が生じるため、例外的にエラー画面に遷移
        throw new Error(
          `${ToSetProfileStatusJap(
            responseSetLiveProfileTmp.data.errorCode
          )}: ${responseSetLiveProfileTmp.data.message}`
        );
      }

      // 正常終了
      dispatch(setLiveRegisterStatus(UI_STATUS.REDIRECT));
      return true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // 異常終了
      dispatch(
        setError({
          msg: LIVE_ADD_SAVE_FLOW_ERR_MSG,
          detail: JSON.stringify(error.response?.data || error?.message),
        })
      );
      return false;
    } finally {
      dispatch(setLiveListStatus(UI_STATUS.PROCESSING));
      dispatch(setLiveSaveButtonStatus(UI_STATUS.PROCESSING));
    }
  };

export default LiveSave;
